import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAnalytics } from "../hooks/useAnalytics";
import { getReferralId } from "../hooks/useReferralCapture";
import logger from "../utils/logger";
import { isPremiumTier, isGrandfatheredTier } from "../utils/entitlements";
import videoDownloadManager from "../utils/videoDownloadManager";
import { setTelemetryUser } from "../utils/telemetryDeck";
import { isFunnelPath } from "../utils/funnelPaths";
// import { useAPI } from './APIContext'; // Removed to avoid circular dependency

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { trackSignUp, trackSignIn, trackSignOut, identifyUser, setUserProperties, resetUser } = useAnalytics();
  const [user, setUser] = useState(null);
  // TelemetryDeck identity follows the signed-in user (hashed in the browser).
  useEffect(() => { setTelemetryUser(user?.id); }, [user?.id]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [apiClient, setApiClient] = useState(null);
  const [isExchangingEmailToken, setIsExchangingEmailToken] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    subscriptionType: "NONE",
    managementPlatform: "none",
    isLoaded: false,
  });
  const [costs, setCosts] = useState({
    create_character: null,
    create_story: null,
    create_audiobook: null,
    star_delivery: null,
    voice_mins: null,
    isLoaded: false,
  });
  const [storiesCount, setStoriesCount] = useState(0);
  const [isFirstStoryUser, setIsFirstStoryUser] = useState(false);

  // ---------------------------------------------------------------------
  // Local-device subscription cache (30 min TTL).
  // The backend resolves RevenueCat server-side; we hold the result locally
  // so we don't re-pull it on every tab focus/navigation. Purchases and
  // cancellations force-refresh past this cache (refreshSubscriptionStatus).
  // ---------------------------------------------------------------------
  const SUBSCRIPTION_CACHE_TTL_MS = 30 * 60 * 1000;
  const SUBSCRIPTION_CACHE_PREFIX = "pixley_sub_status_v2_";
  // One-time cleanup of the pre-entitlement cache schema.
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("pixley_sub_status_v1_"))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
  const subscriptionCacheKey = (userId) => `${SUBSCRIPTION_CACHE_PREFIX}${userId}`;

  const readSubscriptionCache = (userId) => {
    try {
      const raw = localStorage.getItem(subscriptionCacheKey(userId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.fetchedAt || !parsed?.subscription) return null;
      if (Date.now() - parsed.fetchedAt > SUBSCRIPTION_CACHE_TTL_MS) return null;
      // Entitlement-era schema only (v2): older shapes are a cache miss.
      if (parsed.subscription.entitlement_active === undefined) return null;
      // A cached "trialing" that has passed its end must not keep the user
      // entitled for up to 30 minutes — treat as a miss and re-resolve.
      const trialEnd = parsed.subscription.trial_ends_at;
      if (parsed.subscription.is_trialing && trialEnd &&
          Date.now() > new Date(trialEnd).getTime()) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const writeSubscriptionCache = (userId, subscription, costs) => {
    try {
      localStorage.setItem(
        subscriptionCacheKey(userId),
        JSON.stringify({ subscription, costs, fetchedAt: Date.now() }),
      );
    } catch {
      // Storage full/unavailable — cache is best-effort only
    }
  };

  const clearSubscriptionCache = (userId) => {
    try {
      if (userId) {
        localStorage.removeItem(subscriptionCacheKey(userId));
      } else {
        Object.keys(localStorage)
          .filter((key) => key.startsWith(SUBSCRIPTION_CACHE_PREFIX))
          .forEach((key) => localStorage.removeItem(key));
      }
    } catch {
      // ignore
    }
  };

  // Helper function to check if user is new (same logic as OnboardingContext)
  const isNewUser = (userData) => {
    if (!userData) return false;

    // Parse persona if it's a JSON string
    let persona = userData.persona;
    if (typeof persona === "string") {
      try {
        persona = JSON.parse(persona);
      } catch (e) {
        // If it's a non-empty string that can't be parsed, consider it as having persona data
        return !persona || persona.trim() === "" || persona === "{}";
      }
    }

    // User is "new" if persona is null, undefined, empty object, or not an object
    const isNew =
      !persona ||
      typeof persona !== "object" ||
      Array.isArray(persona) ||
      Object.keys(persona).length === 0;

    return isNew;
  };

  // Helper function to track authentication event (sign_up vs login)
  const trackAuthEvent = (userData, method) => {
    if (!window.gtag) return;

    const eventName = isNewUser(userData) ? "sign_up" : "login";

    window.gtag("event", eventName, {
      method: method,
      user_id: userData.id,
    });

    logger.log(
      `📊 Tracked ${eventName} event with Google Analytics (method: ${method})`,
    );
  };

  // Enrich PostHog person with user data and persona
  const enrichPersonProperties = (userData) => {
    if (!userData) return;
    let persona = userData.persona;
    if (typeof persona === 'string') {
      try { persona = JSON.parse(persona); } catch { persona = null; }
    }
    const props = {
      star_balance: userData.pixley_stars ?? 0,
      platform: userData.platform || 'WEB',
    };
    if (persona && typeof persona === 'object' && !Array.isArray(persona)) {
      props.child_age_range = persona.child_age || null;
      props.discovery_source = persona.discovery_source || null;
      props.activation_status = 'activated';
    } else {
      props.activation_status = 'not_activated';
    }
    setUserProperties(props);
  };

  // Capture UTM params from URL for attribution
  const captureUtmParams = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
        const val = params.get(key);
        if (val) utms[key] = val;
      }
      const referralId = getReferralId();
      if (referralId) utms.referral_id = referralId;
      if (Object.keys(utms).length > 0) {
        // Use $set_once so first-touch attribution is preserved
        setUserProperties({}, utms);
      }
    } catch (e) {
      logger.warn('Failed to capture UTM params:', e);
    }
  };

  useEffect(() => {
    // Get API client from window (set by APIProvider)
    if (window.pixleyApiClient && !apiClient) {
      setApiClient(window.pixleyApiClient);
    }
  }, [apiClient]);

  useEffect(() => {
    if (apiClient) {
      handleOAuthCallback().then(() => {
        checkAuthStatus();
      });
    }
  }, [apiClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh user data and subscription status when page becomes visible (user returns to tab)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        logger.log(
          "🔄 Page became visible, refreshing user data and subscription status...",
        );
        refreshUserDataAndSubscription();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, user, apiClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpiration = (event) => {
      logger.log("🔐 Token expiration event received:", event.detail);

      // Clear authentication state
      setUser(null);
      setIsAuthenticated(false);

      // Clear API client token as well
      if (apiClient) {
        apiClient.token = null;
      }

      // Show notification to user if Toast component is available
      const toastEvent = new CustomEvent("showToast", {
        detail: {
          message:
            event.detail.message ||
            "Your session has expired. Please sign in again.",
          type: "warning",
        },
      });
      window.dispatchEvent(toastEvent);

      // Redirect to auth page if not already there
      const currentPath = window.location.pathname;
      if (
        currentPath !== "/auth" &&
        !currentPath.startsWith("/share/") &&
        currentPath !== "/" &&
        currentPath !== "/about" &&
        currentPath !== "/privacy-policy" &&
        currentPath !== "/privacypolicy" &&
        currentPath !== "/example"
      ) {
        logger.log("🔄 Redirecting to /auth due to session expiration");
        window.location.href = "/auth";
      }

      logger.log("✅ Authentication state cleared due to token expiration");
    };

    // Add event listener for token expiration
    window.addEventListener("tokenExpired", handleTokenExpiration);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpiration);
    };
  }, [apiClient]);

  // Handle OAuth callback using backend PKCE flow
  const handleOAuthCallback = async () => {
    try {
      // Check for authorization code instead of tokens (more secure)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");

      if (!code) {
        // Fallback: check for tokens in fragment if using implicit flow
        const fragment = window.location.hash;
        if (fragment && fragment.includes("access_token")) {
          return handleImplicitFlowCallback(fragment);
        }
        return; // No OAuth callback data
      }

      logger.log("🔐 Handling OAuth callback with authorization code...");

      // Verify state parameter to prevent CSRF attacks
      const storedState = sessionStorage.getItem("oauth_state");
      if (state && storedState && state !== storedState) {
        logger.error("❌ OAuth state mismatch - potential CSRF attack");
        return;
      }

      // Get stored PKCE code verifier if using PKCE
      const codeVerifier = sessionStorage.getItem("pkce_code_verifier");

      logger.log("🔄 Exchanging authorization code for tokens via backend...");

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Token exchange timeout")), 10000); // 10 second timeout
      });

      const exchangePromise = apiClient.exchangeAuthCodeForTokens(
        code,
        codeVerifier,
        // Match the redirect target sign-in actually used (origin for regular
        // login, origin + /funnel-auth for the web funnel) so the two agree.
        window.location.origin + window.location.pathname,
      );

      const tokenResult = await Promise.race([exchangePromise, timeoutPromise]);

      if (tokenResult.status) {
        logger.log("✅ Tokens obtained securely via authorization code");

        // Clean up stored OAuth data
        sessionStorage.removeItem("oauth_state");
        sessionStorage.removeItem("pkce_code_verifier");

        // Clean the URL
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );

        // Handle successful authentication
        await handleSuccessfulAuth(tokenResult.data);
      } else {
        throw new Error(
          tokenResult.error || "Failed to exchange authorization code",
        );
      }
    } catch (error) {
      logger.error("❌ OAuth callback error:", error);

      // Clean the URL even on error
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Fallback method for implicit flow (less secure but backward compatible)
  const handleImplicitFlowCallback = async (fragment) => {
    try {
      logger.log("🔐 Handling implicit flow callback...");

      // Parse fragment parameters
      const params = new URLSearchParams(fragment.substring(1));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      if (accessToken) {
        logger.log("🔐 Found OAuth tokens, exchanging for JWT...");

        // Exchange Supabase tokens for our JWT
        const exchangeResult = await apiClient.exchangeSupabaseSession(
          accessToken,
          refreshToken,
        );

        if (exchangeResult.status) {
          logger.log("✅ Successfully exchanged tokens");
          await handleSuccessfulAuth(exchangeResult.data);
        } else {
          throw new Error(exchangeResult.error || "Failed to exchange tokens");
        }
      }
    } catch (error) {
      logger.error("❌ Implicit flow callback error:", error);
      throw error;
    }
  };

  // Handle successful authentication after OAuth
  // method: how they signed in, for analytics ('google' unless said otherwise)
  const handleSuccessfulAuth = async (authData, method = "google") => {
    try {
      logger.log("✅ Handling successful authentication...");
      logger.log("🔍 Auth data received:", authData);

      // Definitive new-vs-returning signal from the backend: whether the
      // users row existed before this sign-in auto-created it. Only stashed
      // during a RevenueCat funnel sign-in — detected by the funnel-auth page
      // being the OAuth landing route — so the funnel can route returning
      // accounts differently. Regular sign-ins are untouched.
      if (
        typeof authData.user_already_existed === "boolean" &&
        isFunnelPath(window.location.pathname)
      ) {
        const existed = authData.user_already_existed ? "1" : "0";
        sessionStorage.setItem("pixley_signin_user_existed", existed);
        // Also kept past the tab, like the funnel's other answers
        // (FunnelAuthPage funnelSet), so a return visit still knows
        try {
          localStorage.setItem("pixley_signin_user_existed", existed);
          localStorage.setItem("pixley_funnel_saved_at", String(Date.now()));
        } catch {
          // blocked storage: the tab copy still works
        }
      }

      // Extract token and user from auth data
      const token = authData.token;
      const user = authData.user;

      if (!token || !user) {
        logger.error("❌ Missing token or user data:", {
          token: !!token,
          user: !!user,
        });
        throw new Error("Invalid authentication data received");
      }

      logger.log("🔍 User data from backend:", user);
      logger.log("🔍 Persona in user data:", user.persona);

      // Store the JWT token and complete user data
      apiClient.token = token;
      localStorage.setItem("pixley_auth_token", token);
      localStorage.setItem("pixley_user_data", JSON.stringify(user));

      // Update current user
      setUser(user);
      setIsAuthenticated(true);

      // Track auth event with PostHog (signup or signin)
      if (isNewUser(user)) {
        // sign_up fires server-side from create_user_if_not_exists (no client-side to avoid double-counting)
      } else {
        trackSignIn(method === "google" ? 'google_oauth' : method, true);
      }
      trackAuthEvent(user, method);
      identifyUser(user.id, {
        email: user.email,
        name: user.name,
        platform: user.platform || 'WEB',
      });
      enrichPersonProperties(user);
      captureUtmParams();

      // Add SIGNIN_<TIMESTAMP> tag when user signs in or creates account via OAuth
      if (user?.id && apiClient) {
        const tag = "SIGNIN";
        apiClient
          .addUserDataTag(user.id, tag)
          .then((result) => {
            if (result.status) {
              logger.log(`✅ Added tag ${tag} for user ${user.id}`);
            } else {
              logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
            }
          })
          .catch((error) => {
            logger.error(`❌ Error adding tag ${tag}:`, error);
          });

        // Backfill platform to WEB if NULL (for OAuth users)
        if (!user.platform) {
          logger.log("🌐 Backfilling platform to WEB for OAuth user");
          apiClient
            .updatePlatform(user.id, "WEB")
            .then((result) => {
              if (result.status) {
                logger.log(`✅ Platform backfilled to WEB for user ${user.id}`);
                // Update local user data
                user.platform = "WEB";
                localStorage.setItem("pixley_user_data", JSON.stringify(user));
                setUser(user);
              } else {
                logger.warn(`⚠️ Failed to backfill platform:`, result.error);
              }
            })
            .catch((error) => {
              logger.error(`❌ Error backfilling platform:`, error);
            });
        }
      }

      // Clean the URL by removing query parameters and fragments
      window.history.replaceState({}, document.title, window.location.pathname);

      logger.log("✅ Authentication setup complete");
    } catch (error) {
      logger.error("❌ Error handling successful auth:", error);
    }
  };

  // Email auth token detection — auto-login from email links (?eat= parameter)
  const handleEmailAuthToken = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const emailAuthToken = urlParams.get('eat');

      if (!emailAuthToken || isAuthenticated) return;

      setIsExchangingEmailToken(true);
      logger.log('🔐 Email auth token detected, exchanging...');

      const response = await apiClient.exchangeEmailAuthToken(emailAuthToken);
      if (response?.data?.access_token) {
        const token = response.data.access_token;
        const userData = response.data.user;

        apiClient.token = token;
        localStorage.setItem('pixley_auth_token', token);
        if (userData) {
          localStorage.setItem('pixley_user_data', JSON.stringify(userData));
          setUser(userData);
        }
        setIsAuthenticated(true);

        // Clean the URL (remove ?eat= param, preserve path)
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('eat');
        window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);

        logger.log('✅ Email auth token exchanged successfully');

        // Load subscription status if we have user data
        if (userData?.id) {
          await loadSubscriptionStatus(userData.id);
        }
      }
    } catch (error) {
      logger.error('❌ Email auth token exchange failed:', error);
      // Token expired or invalid — clean the URL and let normal auth flow handle it
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('eat');
      window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
    } finally {
      setIsExchangingEmailToken(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      setLoading(true);

      // Check for stored auth token
      const token = localStorage.getItem("pixley_auth_token");
      const userData = localStorage.getItem("pixley_user_data");

      if (token && userData && apiClient) {
        const parsedUser = JSON.parse(userData);
        logger.log("🔍 Loaded user from localStorage:", parsedUser);

        apiClient.token = token; // Set token on API client
        setUser(parsedUser);
        setIsAuthenticated(true);

        // Identify returning user in PostHog on every session restore
        identifyUser(parsedUser.id, {
          email: parsedUser.email,
          name: parsedUser.name,
          platform: parsedUser.platform || 'WEB',
        });
        enrichPersonProperties(parsedUser);

        // Show the app IMMEDIATELY from the cached user. Do NOT block the global
        // loading screen on backend calls — a slow/hung getSession or subscription
        // request (e.g. backend slow or restarting) used to freeze the whole app on
        // "Loading..." for a long time. Verify the session + refresh in the background.
        setLoading(false);

        (async () => {
          try {
            const sessionResult = await apiClient.getSession();
            if (!sessionResult.status) {
              logger.log("Session invalid, signing out");
              await signOut();
              return;
            }
            if (sessionResult.data) {
              if (sessionResult.data.user) {
                updateUser(sessionResult.data.user);
              } else if (sessionResult.data.pixley_stars !== undefined) {
                updateUser({ ...parsedUser, pixley_stars: sessionResult.data.pixley_stars });
              }
            }
            await loadSubscriptionStatus(parsedUser.id);
          } catch (error) {
            // Keep the cached session on transient failures — don't freeze or boot the user.
            logger.log("Background session check failed (keeping cached session):", error);
          }
        })();
        return;
      }

      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      logger.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);

      if (!apiClient) {
        throw new Error("API client not available");
      }

      const response = await apiClient.signIn(email, password);

      if (response.status) {
        const { user: userData, token } = response.data;

        logger.log("🔍 User data from sign in:", userData);
        logger.log("🔍 Persona in user data:", userData.persona);

        // Store auth data (backend should return complete user data including persona)
        apiClient.token = token;
        localStorage.setItem("pixley_auth_token", token);
        localStorage.setItem("pixley_user_data", JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);

        // Track sign in with PostHog and GA
        trackSignIn('email', true);
        identifyUser(userData.id, {
          email: userData.email,
          name: userData.name,
          platform: userData.platform || 'WEB',
        });
        enrichPersonProperties(userData);
        trackAuthEvent(userData, "email");

        // Add SIGNIN_<TIMESTAMP> tag when user signs in
        if (userData?.id && apiClient) {
          const tag = "SIGNIN";
          apiClient
            .addUserDataTag(userData.id, tag)
            .then((result) => {
              if (result.status) {
                logger.log(`✅ Added tag ${tag} for user ${userData.id}`);
              } else {
                logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
              }
            })
            .catch((error) => {
              logger.error(`❌ Error adding tag ${tag}:`, error);
            });

          // Backfill platform to WEB if NULL (for existing web users)
          if (!userData.platform) {
            logger.log("🌐 Backfilling platform to WEB for existing user");
            apiClient
              .updatePlatform(userData.id, "WEB")
              .then((result) => {
                if (result.status) {
                  logger.log(
                    `✅ Platform backfilled to WEB for user ${userData.id}`,
                  );
                  // Update local user data
                  userData.platform = "WEB";
                  localStorage.setItem(
                    "pixley_user_data",
                    JSON.stringify(userData),
                  );
                  setUser(userData);
                } else {
                  logger.warn(`⚠️ Failed to backfill platform:`, result.error);
                }
              })
              .catch((error) => {
                logger.error(`❌ Error backfilling platform:`, error);
              });
          }
        }

        return { success: true };
      } else {
        throw new Error(response.error || "Sign in failed");
      }
    } catch (error) {
      logger.error("Sign in error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, name) => {
    try {
      setLoading(true);

      if (!apiClient) {
        throw new Error("API client not available");
      }

      // Get referral ID from localStorage (if user came via referral link)
      const referrerId = getReferralId();

      const response = await apiClient.signUp(email, password, name, referrerId);

      if (response.status) {
        const { user: userData, token } = response.data;

        logger.log("🔍 User data from sign up:", userData);
        logger.log("🔍 Persona in user data:", userData.persona);

        // Store auth data (backend should return complete user data including persona)
        apiClient.token = token;
        localStorage.setItem("pixley_auth_token", token);
        localStorage.setItem("pixley_user_data", JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);

        // Identify user first so PostHog merges anonymous → identified profile
        identifyUser(userData.id, {
          email: userData.email,
          name: userData.name,
          platform: 'WEB',
        });
        enrichPersonProperties(userData);
        captureUtmParams();
        trackAuthEvent(userData, "email");

        // Fire client-side sign_up event with UTM attribution
        // Server-side sign_up event (from create_user_if_not_exists) has source=server
        // This client event carries UTM params that the server event can't access
        try {
          const posthogLib = require('posthog-js').default;
          const utmSource = posthogLib.get_property?.('$initial_utm_source') || '';
          const utmMedium = posthogLib.get_property?.('$initial_utm_medium') || '';
          const utmCampaign = posthogLib.get_property?.('$initial_utm_campaign') || '';
          posthogLib.capture('sign_up', {
            source: 'client',
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            email: userData.email,
          });
        } catch (_) {}

        // Add SIGNIN_<TIMESTAMP> tag when user creates an account (signs up)
        if (userData?.id && apiClient) {
          const tag = "SIGNIN";
          apiClient
            .addUserDataTag(userData.id, tag)
            .then((result) => {
              if (result.status) {
                logger.log(`✅ Added tag ${tag} for user ${userData.id}`);
              } else {
                logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
              }
            })
            .catch((error) => {
              logger.error(`❌ Error adding tag ${tag}:`, error);
            });

          // Set platform to WEB for new web users
          logger.log("🌐 Setting platform to WEB for new user");
          apiClient
            .updatePlatform(userData.id, "WEB")
            .then((result) => {
              if (result.status) {
                logger.log(`✅ Platform set to WEB for user ${userData.id}`);
                // Update local user data
                userData.platform = "WEB";
                localStorage.setItem(
                  "pixley_user_data",
                  JSON.stringify(userData),
                );
                setUser(userData);
              } else {
                logger.warn(`⚠️ Failed to set platform:`, result.error);
              }
            })
            .catch((error) => {
              logger.error(`❌ Error setting platform:`, error);
            });
        }

        return { success: true };
      } else {
        throw new Error(response.error || "Sign up failed");
      }
    } catch (error) {
      logger.error("Sign up error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // frontendUrl (optional) overrides where Supabase sends the browser back
  // after Google auth — the web funnel passes its own /funnel-auth return URL
  // so sign-in lands straight back on the funnel page.
  const signInWithGoogle = async (frontendUrl) => {
    try {
      logger.log("🔐 Starting Google OAuth...");

      if (!apiClient) {
        throw new Error("API client not available");
      }

      logger.log("🌐 Calling backend for Google OAuth URL...");
      // await this.handleOAuthCallback();

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("OAuth request timeout")), 10000); // 10 second timeout
      });

      const apiPromise = apiClient.signInWithGoogle(frontendUrl);
      const result = await Promise.race([apiPromise, timeoutPromise]);

      logger.log("📡 Backend response:", result);

      if (!result.status) {
        throw new Error(result.error || "Failed to get OAuth URL");
      }

      logger.log("✅ Google OAuth URL obtained successfully");

      // The API client should handle the redirect
      // If we reach here, the redirect should have happened
      return { success: true };
    } catch (error) {
      logger.error("❌ Google sign in error:", error);
      return {
        success: false,
        error: error.message || "Failed to sign in with Google",
      };
    }
  };

  // An emailed sign-in link's one-time token (utils/emailLinkSignin) -> the
  // same session a Google sign-in ends in. Resolves to the API's answer
  // ({ok, code, error, data}) so the landing can say what went wrong.
  const signInWithEmailLink = async (token) => {
    if (!apiClient) return { ok: false, code: "network" };
    const result = await apiClient.verifyEmailLink(token);
    if (result.ok && result.data) {
      await handleSuccessfulAuth(result.data, "email_link");
    }
    return result;
  };

  const signOut = async () => {
    try {
      setLoading(true);

      // Track sign out before clearing state
      trackSignOut();
      resetUser();

      // Clear stored auth data
      localStorage.removeItem("pixley_auth_token");
      localStorage.removeItem("pixley_user_data");

      // Clear the local subscription cache so the next account starts fresh
      clearSubscriptionCache();

      // Clean up any active video download polling
      videoDownloadManager.cleanup();

      setUser(null);
      setIsAuthenticated(false);

      // Reset subscription state to defaults for the signed-out session
      setSubscriptionStatus({ subscriptionType: "NONE", isLoaded: false });

      // Optional: Call API to invalidate token on server
      if (apiClient) {
        try {
          await apiClient.signOut();
        } catch (error) {
          logger.log("Server sign out failed (continuing anyway):", error);
        }
      }
    } catch (error) {
      logger.error("Sign out error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("pixley_user_data", JSON.stringify(userData));
  };

  const refreshUserData = async () => {
    if (!apiClient || !user?.id) return;

    try {
      logger.log("🔄 Refreshing user data from backend...");
      const sessionResult = await apiClient.getSession();

      if (sessionResult.status && sessionResult.data) {
        // If session returns full user data, update it
        if (sessionResult.data.user) {
          const freshUserData = sessionResult.data.user;
          logger.log("✅ Refreshed user data:", freshUserData);
          updateUser(freshUserData);
        }
        // If session only returns pixley_stars, update just that field
        else if (sessionResult.data.pixley_stars !== undefined) {
          const updatedUser = {
            ...user,
            pixley_stars: sessionResult.data.pixley_stars,
          };
          logger.log(
            "✅ Refreshed pixley_stars:",
            sessionResult.data.pixley_stars,
          );
          updateUser(updatedUser);
        }
      }
    } catch (error) {
      logger.error("❌ Error refreshing user data:", error);
    }
  };

  const refreshUserDataAndSubscription = async () => {
    if (!apiClient || !user?.id) return;

    try {
      // Refresh user data (stars) freshly; subscription respects the 30-min
      // local cache — it only re-pulls from the backend once expired.
      await Promise.all([refreshUserData(), loadSubscriptionStatus(user.id)]);
      logger.log("✅ Refreshed user data and subscription status");
    } catch (error) {
      logger.error("❌ Error refreshing user data and subscription:", error);
    }
  };

  const loadSubscriptionStatus = async (userId, { force = false } = {}) => {
    if (!userId || !apiClient) return;

    try {
      // Serve subscription + costs from the 30-min local cache unless forced —
      // the backend call (which resolves RevenueCat) only happens on expiry.
      const cached = force ? null : readSubscriptionCache(userId);

      // Stats are always fetched fresh (stars/stories change often)
      const [statsResponse, subscriptionResponse] = await Promise.all([
        apiClient.getUserStats(userId),
        cached
          ? Promise.resolve({ status: true, data: cached.subscription, fromCache: true })
          : apiClient.getSubscriptionStatus(userId, { force }),
      ]);

      if (subscriptionResponse.status && statsResponse.status) {
        const subscription = subscriptionResponse.data;
        const subscriptionType = subscription.subscription || "NONE";
        const stats = statsResponse.data || {};

        const nextStoriesCount = Number(stats.storiesCount ?? 0);
        const nextIsFirstStoryUser = nextStoriesCount === 0;
        logger.log("📚 Story eligibility:", {
          storiesCount: nextStoriesCount,
          isFirstStoryUser: nextIsFirstStoryUser,
        });

        // Enrich PostHog user with subscription tier and content counts
        setUserProperties({
          subscription_type: subscriptionType,
          total_stories: nextStoriesCount,
          total_characters: stats.charactersCount ?? 0,
          total_shows: stats.showsCount ?? 0,
        });
        setStoriesCount(nextStoriesCount);
        setIsFirstStoryUser(nextIsFirstStoryUser);

        // Set subscription status; pass through cancellation fields if present (e.g. Stripe cancel_at_period_end, current_period_end)
        setSubscriptionStatus({
          subscriptionType,
          managementPlatform: subscription.management_platform || "unknown",
          subscriptionSource: subscription.source || null,
          isLoaded: true,
          cancel_at_period_end: subscription.cancel_at_period_end === true || subscription.cancel_at_period_end === "true",
          current_period_end: subscription.current_period_end ?? subscription.current_period_end_date ?? null,
          // Entitlement-era fields (web trial cutover). Fallback keeps the
          // client correct before the backend ships them: tier != NONE.
          entitlementActive:
            subscription.entitlement_active ?? isPremiumTier(subscriptionType),
          isTrialing: subscription.is_trialing === true,
          trialEndsAt: subscription.trial_ends_at ?? null,
          subscriptionRequired: subscription.subscription_required === true,
          trialConfig: subscription.trial_config ?? null,
          introOffer: subscription.intro_offer ?? null,
          // the win-back paywall's terms when checkout would honour one (utils/membershipOffer), else null
          winbackOffer: subscription.winback_offer && typeof subscription.winback_offer === 'object' ? subscription.winback_offer : null,
        });
        logger.log(
          "💳 Subscription status:",
          subscriptionType,
          subscriptionResponse.fromCache ? "(30-min local cache)" : "(from backend)",
        );

        // Costs travel with the cached subscription (they derive from the tier)
        if (subscriptionResponse.fromCache && cached?.costs) {
          setCosts(cached.costs);
        } else {
          let costsForCache = null;
          try {
            const costsResponse = await apiClient.getCosts(
              userId,
              subscriptionType,
            );
            if (costsResponse.status && costsResponse.data?.rate_limits) {
              const rateLimits = costsResponse.data.rate_limits;
              const nextCosts = {
                create_character: rateLimits.create_character ?? null,
                create_story: rateLimits.create_story ?? null,
                create_audiobook: rateLimits.create_audiobook ?? null,
                star_delivery: rateLimits.star_delivery ?? null,
                voice_mins: rateLimits.voice_mins ?? null,
                downloadAbility: rateLimits.downloadAbility ?? null,
                isLoaded: true,
              };
              setCosts(nextCosts);
              costsForCache = nextCosts;
              logger.log("✅ Loaded costs:", rateLimits);
            }
          } catch (error) {
            logger.error("Error loading costs:", error);
            setCosts((prev) => ({ ...prev, isLoaded: true }));
          }
          if (!subscriptionResponse.fromCache) {
            writeSubscriptionCache(userId, subscription, costsForCache);
          }
        }
      } else {
        setSubscriptionStatus((prev) => ({ ...prev, isLoaded: true }));
        setCosts((prev) => ({ ...prev, isLoaded: true }));
        setStoriesCount(0);
        setIsFirstStoryUser(false);
      }
    } catch (error) {
      logger.error("Error loading subscription status:", error);
      setSubscriptionStatus((prev) => ({ ...prev, isLoaded: true }));
      setCosts((prev) => ({ ...prev, isLoaded: true }));
      setStoriesCount(0);
      setIsFirstStoryUser(false);
    }
  };

  // Force-refresh subscription status, bypassing the 30-min local cache.
  // Used after purchases, upgrades, and cancellations.
  const refreshSubscriptionStatus = async () => {
    if (user?.id) {
      await loadSubscriptionStatus(user.id, { force: true });
    }
  };

  const syncAndRefreshSubscription = async () => {
    if (user?.id && apiClient) {
      try {
        // Ask the backend to re-pull RevenueCat and restamp the premium window
        await apiClient.syncSubscription(user.id);
        // Then force-refresh past the local cache
        await loadSubscriptionStatus(user.id, { force: true });
      } catch (error) {
        logger.error("Error syncing subscription:", error);
        // Still try to refresh subscription status even if sync fails
        await loadSubscriptionStatus(user.id, { force: true });
      }
    }
  };

  // Poll until a just-purchased subscription becomes visible in RevenueCat.
  // Stripe→RevenueCat ingestion can lag the checkout redirect by seconds, so
  // a single refresh can catch NONE and cache it for 30 minutes; this polls
  // (cache-busted on both client and server) until the entitlement lands or
  // the timeout passes. Safe to call fire-and-forget.
  const activationPollActive = useRef(false);

  const waitForSubscriptionActivation = async ({ timeoutMs = 90000, intervalMs = 5000, untilChangedFrom = null } = {}) => {
    if (!user?.id || !apiClient) return false;
    if (activationPollActive.current) return false; // a poll is already running
    activationPollActive.current = true;

    // Default: wait for any subscription (post-checkout). With
    // untilChangedFrom set, wait for the tier to CHANGE (post tier-switch).
    const isActivated = (tier) =>
      untilChangedFrom ? tier !== "NONE" && tier !== untilChangedFrom : tier !== "NONE";

    const startedAt = Date.now();
    try {
      while (Date.now() - startedAt < timeoutMs) {
        try {
          const response = await apiClient.getSubscriptionStatus(user.id, { force: true });
          const tier = response?.data?.subscription || "NONE";
          if (response?.status && isActivated(tier)) {
            logger.log("✅ Subscription activation detected:", tier);
            await loadSubscriptionStatus(user.id, { force: true });
            return true;
          }
          logger.log("⏳ Subscription not visible in RevenueCat yet, polling...");
        } catch (error) {
          logger.warn("Subscription activation poll failed:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      logger.warn("⏳ Subscription activation not visible before timeout; refreshing state anyway");
      await loadSubscriptionStatus(user.id, { force: true });
      return false;
    } finally {
      activationPollActive.current = false;
    }
  };

  // Entitlement-era derived state (web trial cutover): the single booleans
  // every gate site should consume instead of tier arrays.
  const hasEntitlement =
    subscriptionStatus.isLoaded && subscriptionStatus.entitlementActive === true;
  const isGrandfathered = isGrandfatheredTier(subscriptionStatus.subscriptionType);
  const isTrialing = subscriptionStatus.isTrialing === true;

  // Backstop: any API call rejected with SUBSCRIPTION_REQUIRED means our
  // local picture is stale — re-resolve immediately (the gate context opens
  // the wall off the same event).
  useEffect(() => {
    const onSubRequired = () => {
      if (user?.id) {
        loadSubscriptionStatus(user.id, { force: true });
      }
    };
    window.addEventListener("pixley:subscription-required", onSubRequired);
    return () =>
      window.removeEventListener("pixley:subscription-required", onSubRequired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = {
    user,
    loading,
    isAuthenticated,
    isExchangingEmailToken,
    subscriptionStatus,
    hasEntitlement,
    isGrandfathered,
    isTrialing,
    costs,
    storiesCount,
    isFirstStoryUser,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithEmailLink,
    signOut,
    updateUser,
    refreshUserData,
    refreshUserDataAndSubscription,
    checkAuthStatus,
    refreshSubscriptionStatus,
    syncAndRefreshSubscription,
    waitForSubscriptionActivation,
    setIsFirstStoryUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
